import Toggle from '@ui/Toggle';
import { UserDocument, UserSizes } from 'models/user';
import { HelmetIcon } from 'i/measurement/helmet-icon';
import { GearIcon } from 'i/measurement/gear-icon';
import { BootsIcon } from 'i/measurement/boot-icon';
import PantIcon from 'i/measurement/pant-icon';
import EditSizesModal from './EditSizesModal';
import MobileEditSizesModal from './MobileEditSizesModal';
import { AnimatedList, EmptyAnimatedList } from './AnimatedList';
import { isMobile } from '@util/index';

type GearFilterProps = {
  isMySizesEnabled: boolean;
  setIsMySizesEnabled: (enabled: boolean) => void;
  isEditSizesModalOpen: boolean;
  setIsEditSizesModalOpen: (open: boolean) => void;
  isMobileEditSizesModalOpen: boolean;
  setIsMobileEditSizesModalOpen: (open: boolean) => void;
  userDoc: UserDocument;
  userSizesFlatList: {
    type: keyof UserSizes;
    value: string | number | undefined;
    enabled: boolean;
  }[];
  selectedSizes: UserSizes | null;
  onSelectSize: (size: keyof UserSizes) => void;
  handleSizeSelection: (sizes: UserSizes) => void;
};

export const GearFilter = ({
  isMySizesEnabled,
  setIsMySizesEnabled,
  isEditSizesModalOpen,
  setIsEditSizesModalOpen,
  isMobileEditSizesModalOpen,
  setIsMobileEditSizesModalOpen,
  userDoc,
  userSizesFlatList,
  onSelectSize,
  handleSizeSelection,
}: GearFilterProps) => {
  return (
    <div className="flex h-fit w-full flex-col items-start gap-6 bg-[#F6F6F6] p-6">
      <div className="flex w-full items-center justify-between">
        <h3 className="text-[1.8rem] font-semibold text-black">My sizes</h3>

        <Toggle
          enabled={isMySizesEnabled}
          onChange={() => setIsMySizesEnabled(!isMySizesEnabled)}
        />
      </div>

      <p className="text-[1.6rem] font-medium text-[#7F7F7F]">
        Enable this to only see items available in your saved sizes.
      </p>

      <button
        type="button"
        className="bg-transparent text-[1.6rem] font-semibold capitalize text-brand-secondary transition-colors duration-150 hover:text-brand-secondary-darker"
        onClick={() => {
          if (isMobile()) {
            setIsMobileEditSizesModalOpen(true);
          } else {
            setIsEditSizesModalOpen(true);
          }
        }}
      >
        Edit
      </button>

      {userSizesFlatList.length ? (
        <AnimatedList isEnabled={isMySizesEnabled}>
          {userSizesFlatList.map(
            (
              size: {
                type: keyof UserSizes;
                value: string | number | undefined;
                enabled: boolean;
              },
              idx: number
            ) => {
              const iconMap: Record<string, JSX.Element> = {
                helmet: <HelmetIcon className="mr-3 h-[3rem] w-[3rem]" />,
                jersey: <GearIcon className="mr-3 h-[3rem] w-[3rem]" />,
                pants: <PantIcon className="mr-3 h-[3rem] w-[3rem]" />,
                boots: <BootsIcon className="mr-3 h-[3rem] w-[3rem]" />,
              };

              return (
                <div
                  className="flex w-full items-center justify-between border-t border-[#D9D9D9] pt-4"
                  key={`user-size-${size.type}-${idx}`}
                >
                  <div className="flex items-center gap-x-4 capitalize">
                    {iconMap[size.type as string]}

                    <p className="whitespace-nowrap text-[1.8rem] font-medium text-black sm:font-semibold">
                      {size.type === 'jersey' ? 'Jersey / Shirt' : size.type}
                    </p>
                  </div>

                  <p className="w-full pr-8 text-right text-[1.6rem] text-[#7F7F7F]">
                    {size.value}
                  </p>

                  <Toggle
                    enabled={size.enabled}
                    onChange={() => onSelectSize(size.type)}
                  />
                </div>
              );
            }
          )}
        </AnimatedList>
      ) : (
        <EmptyAnimatedList isEnabled={isMySizesEnabled} type="sizes" />
      )}

      <EditSizesModal
        isOpen={!isMobile() && isEditSizesModalOpen}
        dismiss={() => setIsEditSizesModalOpen(false)}
        userDoc={userDoc}
      />

      <MobileEditSizesModal
        open={isMobile() && isMobileEditSizesModalOpen}
        dismiss={() => setIsMobileEditSizesModalOpen(false)}
        userDoc={userDoc}
        handleSizeSelection={handleSizeSelection}
      />
    </div>
  );
};
